import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './AddApplicationProfile.scss';
import {
  MotifFormField,
  MotifLabel,
  MotifInput,
  MotifSelect,
  MotifOption,
  MotifButton,
  MotifAccordion,
  MotifAccordionTrigger,
  MotifAccordionContent,
} from '@ey-xd/motif-react';
import { MotifErrorMessage } from '@ey-xd/motif-react';
import TextArea from 'common/components/TextArea/TextArea';
import { MotifIcon } from '@ey-xd/motif-react';
import { actionIcDelete24px, navigationIcArrowBack24px } from '@ey-xd/motif-react/assets/icons';
import { MAX_INPUT_LENGTH } from 'components/Clients/constants';
import { getApplicationProfiles, getTestTypes, getBusinessUnits, createApplicationProfile as createApplicationProfileAction, updateApplicationProfile as updateApplicationProfileAction } from '../../redux/actions/ApplicationProfile/applicationProfile';

export type KeyedText = { value: string; description?: string; httpMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE' };

export interface AddApplicationProfileData {
  testType?: string;
  applicationName?: string;
  businessUnit?: string;
  testingEnvironment?: string;
  workOrder?: string | number;
  urls?: KeyedText[];
  ips?: KeyedText[];
  endpoints?: KeyedText[];
  binaryChecksums?: KeyedText[];
  servers?: KeyedText[];
  ApplicationVersion?: string;
  userRoles?: string;
  testData?: string;
  description?: string;
}

interface AddApplicationProfileProps {
  initialData?: AddApplicationProfileData;
  mode?: 'add' | 'edit';
  onCancel?: () => void;
  onSubmit?: (message: string, status: string) => void;
}

const TEST_TYPES = [
  'Web Application Penetration Testing',
  'Android Application Penetration Testing',
  'iOS Application Penetration Testing',
  'Web Service Application Penetration Testing',
  'Thick Client Application Penetration Testing',
];

type SectionKey =
  | 'urls'
  | 'ips'
  | 'endpoints'
  | 'binaryChecksums'
  | 'servers';

type FormConfig = {
  listSections: Array<{ key: SectionKey; title: string; valueLabel: string; descLabel: string; addLabel: string, isRequired: boolean }>;
  showApplicationVersion: boolean;
  showUserRoles: boolean;
  showTestData: boolean;
  showDescription: boolean;
};

const CONFIG_BY_TESTTYPE: Record<string, FormConfig> = {
  'Web Application Penetration Testing': {
    listSections: [
      { key: 'urls', title: 'Application URL\'s', valueLabel: 'Please provide Application URL', descLabel: 'Please provide Description', addLabel: 'Add URL', isRequired: true },
      { key: 'ips', title: 'Application IP\'s', valueLabel: 'Please provide Application IP', descLabel: 'Please provide Description', addLabel: 'Add IP', isRequired: false },
    ],
    showApplicationVersion: false,
    showUserRoles: true,
    showTestData: true,
    showDescription: true,
  },
  'Android Application Penetration Testing': {
    listSections: [
      { key: 'binaryChecksums', title: 'Application Binary Checksum', valueLabel: 'Please provide Application Binary Checksum', descLabel: 'Please provide Description', addLabel: 'Add Binary Checksum', isRequired: true },
      { key: 'servers', title: 'Application Server', valueLabel: 'Please provide Application Server', descLabel: 'Please provide Description', addLabel: 'Add Application Server', isRequired: true },
      { key: 'ips', title: 'Application IP Address', valueLabel: 'Please provide Application IP Address', descLabel: 'Please provide Description', addLabel: 'Add Application IP Address', isRequired: false },
    ],
    showApplicationVersion: true,
    showUserRoles: true,
    showTestData: true,
    showDescription: true,
  },
  'iOS Application Penetration Testing': {
    listSections: [
      { key: 'binaryChecksums', title: 'Application Binary Checksum', valueLabel: 'Please provide Application Binary Checksum', descLabel: 'Please provide Description', addLabel: 'Add Binary Checksum', isRequired: true },
      { key: 'servers', title: 'Application Server', valueLabel: 'Please provide Application Server', descLabel: 'Please provide Description', addLabel: 'Add Application Server', isRequired: true },
      { key: 'ips', title: 'Application IP Address', valueLabel: 'Please provide Application IP Address', descLabel: 'Please provide Description', addLabel: 'Add Application IP Address', isRequired: false },
    ],
    showApplicationVersion: true,
    showUserRoles: true,
    showTestData: true,
    showDescription: true,
  },
  'Web Service Application Penetration Testing': {
    listSections: [
      { key: 'endpoints', title: 'Application Endpoints', valueLabel: 'Please provide Application Endpoints', descLabel: 'Please provide Description', addLabel: 'Add Application Endpoints', isRequired: true },
      { key: 'ips', title: 'Application IP Address', valueLabel: 'Please provide Application IP Address', descLabel: 'Please provide Description', addLabel: 'Add Application IP Address', isRequired: false },
    ],
    showApplicationVersion: false,
    showUserRoles: true,
    showTestData: false,
    showDescription: true,
  },
  'Thick Client Application Penetration Testing': {
    listSections: [],
    showApplicationVersion: false,
    showUserRoles: true,
    showTestData: false,
    showDescription: true,
  },
};

const emptyArrayField = (): KeyedText => ({ value: '', description: '' });

const AddApplicationProfile: React.FC<AddApplicationProfileProps> = ({
  initialData,
  mode = 'add',
  onCancel,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const currentTenant = useSelector((state: any) => state?.user?.currentTenant);
  const isAiaTenant = (currentTenant?.TenantName || '').toLowerCase() === 'aia';
  const [form, setForm] = useState<AddApplicationProfileData>(() => ({
    testType: initialData?.testType || '',
    applicationName: initialData?.applicationName || '',
    businessUnit: initialData?.businessUnit || '',
    testingEnvironment: initialData?.testingEnvironment || '',
    workOrder: String(initialData?.workOrder ?? ''),
    urls: initialData?.urls || [emptyArrayField()],
    ips: initialData?.ips || [emptyArrayField()],
    endpoints: initialData?.endpoints || [emptyArrayField()],
    binaryChecksums: initialData?.binaryChecksums || [emptyArrayField()],
    servers: initialData?.servers || [emptyArrayField()],
    ApplicationVersion: initialData?.ApplicationVersion || '',
    userRoles: (initialData as any)?.userRoles || (initialData as any)?.userRoles || '',
    testData: initialData?.testData || '',
    description: initialData?.description || '',
  }));

  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<{ testType: boolean; applicationName: boolean; businessUnit: boolean; userRoles: boolean; ApplicationVersion: boolean; workOrder: boolean }>({
    testType: false,
    applicationName: false,
    businessUnit: false,
    userRoles: false,
    ApplicationVersion: false,
    workOrder: false,
  });

  const markTouched = (key: keyof typeof touched) => setTouched(prev => ({ ...prev, [key]: true }));

  useEffect(() => {
    if (initialData) {
      setForm({
        testType: initialData.testType || '',
        applicationName: initialData.applicationName || '',
        businessUnit: initialData.businessUnit || '',
        testingEnvironment: initialData.testingEnvironment || '',
        workOrder: String(initialData.workOrder ?? ''),
        urls: initialData.urls?.length ? initialData.urls : [emptyArrayField()],
        ips: initialData.ips?.length ? initialData.ips : [emptyArrayField()],
        endpoints: initialData.endpoints?.length ? initialData.endpoints : [emptyArrayField()],
        binaryChecksums: initialData.binaryChecksums?.length ? initialData.binaryChecksums : [emptyArrayField()],
        servers: initialData.servers?.length ? initialData.servers : [emptyArrayField()],
        ApplicationVersion: initialData.ApplicationVersion || '',
        userRoles: (initialData as any)?.userRoles || (initialData as any)?.userRoles || '',
        testData: initialData.testData || '',
        description: initialData.description || '',
      });
    }
  }, [initialData]);


  const activeConfig: FormConfig | undefined = useMemo(() => {
    return form.testType ? CONFIG_BY_TESTTYPE[form.testType] : undefined;
  }, [form.testType]);

  useEffect(() => {
    // Reset validation state when test type changes to avoid stale errors
    setSubmitted(false);
  }, [form.testType]);

  // ---------- Validators ----------
  const isIPv4 = (val: string): boolean => {
    const ipv4 = /^(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
    return ipv4.test(val.trim());
  };

  const hasHttpScheme = (val: string): boolean => /^https?:\/\//i.test(val.trim());
  const isHttpUrl = (val: string): boolean => {
    const s = val.trim();
    if (!hasHttpScheme(s)) return false;
    try {
      const u = new URL(s);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isDomainLabel = (label: string): boolean => /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)$/.test(label);
  const isHostname = (val: string): boolean => {
    const s = val.trim();
    if (s.toLowerCase() === 'localhost') return true;
    if (s.length > 253) return false;
    const parts = s.split('.');
    if (parts.length < 2) return false;
    return parts.every(isDomainLabel);
  };

  const isUrlOrDomain = (val: string): boolean => isHttpUrl(val) || isHostname(val);
  const isUrlOrPath = (val: string): boolean => isHttpUrl(val) || /^\/.+/.test(val.trim());

  const isHexOfLengths = (val: string, lengths: number[]): boolean => {
    const s = val.trim();
    return lengths.some(len => new RegExp(`^[A-Fa-f0-9]{${len}}$`).test(s));
  };

  // Application Version must be in X.Y format (e.g., 1.0, 2.1)
  const isAppVersion = (val: string): boolean => /^\d+\.\d+$/.test(val.trim());

  const computeListErrors = (
    cfg: FormConfig | undefined,
    currentForm: AddApplicationProfileData
  ): { errors: Partial<Record<SectionKey, string[]>>; sectionMessages: Partial<Record<SectionKey, string>>; isValid: boolean } => {
    const errors: Partial<Record<SectionKey, string[]>> = {};
    const sectionMessages: Partial<Record<SectionKey, string>> = {};
    let isValid = true;

    if (!cfg) return { errors, sectionMessages, isValid: true };

    cfg.listSections.forEach(section => {
      const arr = (currentForm[section.key] as KeyedText[]) || [];
      const sectionErrors: string[] = Array(arr.length).fill('');

      const allEmpty = arr.every(it => (it?.value || '').trim().length === 0);
      // Make IP Address section optional: do not require at least one entry
      const isOptionalSection = section.key === 'ips';
      if (allEmpty && !isOptionalSection) {
        sectionMessages[section.key] = 'Please add at least one entry.';
        isValid = false;
      }

      arr.forEach((it, idx) => {
        const value = (it?.value || '').trim();
        const valueEmpty = value.length === 0;
        const descriptionFilled = (it?.description || '').trim().length > 0;
        if (!allEmpty) {
          // If there is at least one filled row, any partially filled row needs a value
          if (valueEmpty && descriptionFilled) {
            sectionErrors[idx] = 'This field is required.';
            isValid = false;
          }

          // Format validations for non-empty values
          if (!valueEmpty) {
            let ok = true;
            let msg = '';
            switch (section.key) {
              case 'urls': {
                ok = isUrlOrDomain(value);
                msg = 'Enter a valid URL or domain.';
                break;
              }
              case 'endpoints': {
                ok = isUrlOrPath(value);
                msg = 'Enter a valid URL or path starting with /';
                break;
              }
              case 'ips': {
                ok = isIPv4(value);
                msg = 'Enter a valid IP address.';
                break;
              }
              case 'servers': {
                ok = isIPv4(value) || isHostname(value);
                msg = 'Enter a valid server IP or Domain.';
                break;
              }
              case 'binaryChecksums': {
                ok = isHexOfLengths(value, [32, 40, 64, 96, 128]);
                msg = 'Enter a valid hex checksum (MD5/SHA1/SHA256/SHA384/SHA512).';
                break;
              }
              default:
                ok = true;
            }
            if (!ok) {
              sectionErrors[idx] = msg;
              isValid = false;
            }
          }
        }
      });

      // Check for duplicate values (case-insensitive comparison)
      if (!allEmpty) {
        const filledValues: Map<string, number[]> = new Map();
        arr.forEach((it, idx) => {
          const value = (it?.value || '').trim();
          if (value.length > 0) {
            // For endpoints, include httpMethod in the duplicate check
            let duplicateKey = value.toLowerCase();
            if (section.key === 'endpoints') {
              const httpMethod = (it?.httpMethod || '').trim();
              duplicateKey = `${value.toLowerCase()}::${httpMethod}`;
            }
            
            if (!filledValues.has(duplicateKey)) {
              filledValues.set(duplicateKey, []);
            }
            filledValues.get(duplicateKey)!.push(idx);
          }
        });

        // Mark all duplicate entries with error
        filledValues.forEach((indices, duplicateKey) => {
          if (indices.length > 1) {
            indices.forEach(idx => {
              // Only set duplicate error if there's no existing error
              if (!sectionErrors[idx]) {
                sectionErrors[idx] = 'Duplicate value. Each entry must be unique.';
                isValid = false;
              }
            });
          }
        });
      }

      errors[section.key] = sectionErrors;
    });

    return { errors, sectionMessages, isValid };
  };

  const listValidation = useMemo(() => computeListErrors(activeConfig, form), [activeConfig, form]);

  const updateForm = (patch: Partial<AddApplicationProfileData>) => {
    setForm(prev => ({ ...prev, ...patch }));
    if (submitted) setSubmitted(false);
  };

  const setArrayField = (key: SectionKey, next: KeyedText[]) => {
    setForm(prev => ({ ...prev, [key]: next }));
    if (submitted) setSubmitted(false);
  };

  const addArrayItem = (key: SectionKey) => {
    const arr = (form[key] as KeyedText[]) || [];
    setArrayField(key, [...arr, emptyArrayField()]);
  };

  const removeArrayItem = (key: SectionKey, index: number) => {
    const arr = (form[key] as KeyedText[]) || [];
    const next = arr.filter((_, i) => i !== index);
    setArrayField(key, next.length ? next : [emptyArrayField()]);
  };

  const updateArrayItem = (key: SectionKey, index: number, patch: Partial<KeyedText>) => {
    const arr = (form[key] as KeyedText[]) || [];
    const next = arr.map((item, i) => (i === index ? { ...item, ...patch } : item));
    setArrayField(key, next);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    const hasTopLevelErrors =
      !form.testType ||
      !form.applicationName ||
      !form.businessUnit ||
      !form.userRoles ||
      (isAiaTenant && !String(form.workOrder ?? '').trim()) ||
      (activeConfig?.showApplicationVersion &&
        form.ApplicationVersion &&
        !isAppVersion(form.ApplicationVersion || ''));
    if (hasTopLevelErrors || !listValidation.isValid) {
      return;
    }
    // Build payload expected by API from form state and dropdown selections
    const tt = (testTypes as any[])?.find((t: any) => t.TestTypeName === form.testType);
    const bu = (businessUnits as any[])?.find((b: any) => b.BusinessUnitName === form.businessUnit);

    const mapList = (items: KeyedText[] | undefined, key: string) =>
      (items || [])
        .filter(it => (it?.value || '').trim().length > 0)
        .map(it => ({ [key]: it.value, description: it.description || '' }));


    const endpointsPayload = (form.endpoints || [])
      .filter(it => (it?.value || '').trim().length > 0)
      .map(it => {
        const obj: any = { endpoint: it.value, description: it.description || '' };
        if (it.httpMethod) obj.httpMethod = it.httpMethod;
        return obj;
      });

    const payload: any = {
      application: {
        ApplicationName: form.applicationName || '',
        ApplicationVersion: form.ApplicationVersion || '',
        BusinessUnitId: bu?.BusinessUnitId || '',
        Description: form.description || '',
        TestTypeId: tt?.TestTypeId || '',
        UserRoles: form.userRoles || '',
        TestingEnvironment: form.testingEnvironment || '',
        ...(isAiaTenant ? { WorkOrder: form.workOrder || '' } : {}),
      },
      binary_checksums: mapList(form.binaryChecksums, 'checksum'),
      ips: mapList(form.ips, 'ip'),
      servers: mapList(form.servers, 'server'),
      urls: mapList(form.urls , 'url'),
      endpoints: endpointsPayload,
    };

    const fallbackMsg = mode === 'edit' ? 'Application profile updated successfully.' : 'Application profile created successfully.';
    if (mode === 'edit' && (initialData as any)?.ApplicationId) {
      const id = (initialData as any).ApplicationId as string;
      dispatch(updateApplicationProfileAction(id, payload))
        .then((res: any) => dispatch(getApplicationProfiles()).then(() => res))
        .then((res: any) => {
          const apiMsg = res?.response?.data?.message || fallbackMsg;
          onSubmit && onSubmit(apiMsg, 'success');
        })
        .catch((err: any) => {
          const errMsg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to update application profile. Please try again.';
          onSubmit && onSubmit(errMsg, 'error');
        });
    } else {
      dispatch(createApplicationProfileAction(payload))
        .then((res: any) => dispatch(getApplicationProfiles()).then(() => res))
        .then((res: any) => {
          const apiMsg = res?.response?.data?.message || fallbackMsg;
          onSubmit && onSubmit(apiMsg, 'success');
        })
        .catch((err: any) => {
          const errMsg = err?.response?.data?.error || 'Failed to create application profile. Please try again.';
          onSubmit && onSubmit(errMsg, 'error');
        });
    }
  };

  const headingText = form.testType || 'Add Application Profile';

  const testTypes: any[] = useSelector((state: any) => state?.applicationProfile?.testTypes?.data || state?.applicationProfile?.testTypes || []) || [];
  const businessUnits: any[] = useSelector((state: any) => state?.applicationProfile?.businessUnits?.data || state?.applicationProfile?.businessUnits || []) || [];

  return (
    <div className="addApplicationProfilePage" role="form" aria-label="Add Application Profile">
      {/* <div className="addApplicationProfile__headingBar">{headingText}</div> */}
        <MotifButton
          className="addApplicationProfile__backBtn"
          variant="secondary"
          size="small"
          type="button"
          onClick={onCancel}
          aria-label="Back"
        >
          <MotifIcon src={navigationIcArrowBack24px} style={{ width: 20, marginRight: 6 }} />
          Back
        </MotifButton>
        <div className="addApplicationProfile__title">{mode === 'edit' ? 'Edit Application Profile' : 'Add Application Profile'}</div>

      <div className="addApplicationProfile__card">
        <MotifAccordion type="single" collapsible>
          <MotifAccordionTrigger>Data Attributes Properties</MotifAccordionTrigger>
          <MotifAccordionContent>
            <div className="addApplicationProfile__grid" style={{ marginTop: 8 }}>
              <div>
                <span>Test Type (required)</span>
                <MotifFormField>
                  {!form.testType && <MotifLabel>Select Test Type</MotifLabel>}
                  <MotifSelect value={form.testType} onChange={(v: any) => updateForm({ testType: v })} onBlur={() => markTouched('testType')} visibleOptions={4} disabled={isAiaTenant && mode === 'edit'}>
                    {testTypes.map((tt: any) => (
                      <MotifOption key={tt.TestTypeId} value={tt.TestTypeName}>{tt.TestTypeName}</MotifOption>
                    ))}
                  </MotifSelect>
                </MotifFormField>
                {((touched.testType || submitted) && !form.testType) && (
                  <MotifErrorMessage role="alert">This field is required.</MotifErrorMessage>
                )}
              
                <span>Application Name (required)</span>
                <MotifFormField>
                  {!form.applicationName && <MotifLabel>Type Application Name</MotifLabel>}
                  <MotifInput
                    value={form.applicationName || ''}
                    maxLength={MAX_INPUT_LENGTH}
                    onChange={(e: any) => updateForm({ applicationName: e.target.value })}
                    onBlur={() => markTouched('applicationName')}
                  />
                </MotifFormField>
                {((touched.applicationName || submitted) && !form.applicationName) && (
                  <MotifErrorMessage role="alert">This field is required.</MotifErrorMessage>
                )}

                {isAiaTenant && (
                  <>
                    <span>Work Order Number (required)</span>
                    <MotifFormField>
                      {!form.workOrder && <MotifLabel>Type Work Order Number</MotifLabel>}
                      <MotifInput
                        value={String(form.workOrder ?? '')}
                        maxLength={MAX_INPUT_LENGTH}
                        disabled={mode === 'edit'}
                        onChange={(e: any) => updateForm({ workOrder: e.target.value })}
                        onBlur={() => markTouched('workOrder')}
                      />
                    </MotifFormField>
                    {((touched.workOrder || submitted) && !String(form.workOrder ?? '').trim()) && (
                      <MotifErrorMessage role="alert">This field is required.</MotifErrorMessage>
                    )}
                  </>
                )}
              
                <span>Business Unit (required)</span>
                <MotifFormField>
                  {!form.businessUnit && <MotifLabel>Select Business Unit</MotifLabel>}
                  <MotifSelect value={form.businessUnit} onChange={(v: any) => updateForm({ businessUnit: v })} onBlur={() => markTouched('businessUnit')} visibleOptions={4}>
                    {businessUnits.map((b: any) => (
                      <MotifOption key={b.BusinessUnitId} value={b.BusinessUnitName}>{b.BusinessUnitName}</MotifOption>
                    ))}
                  </MotifSelect>
                </MotifFormField>
                {((touched.businessUnit || submitted) && !form.businessUnit) && (
                  <MotifErrorMessage role="alert">This field is required.</MotifErrorMessage>
                )}

                <span>Testing Environment</span>
                <MotifFormField>
                  {!form.testingEnvironment && <MotifLabel>Type Testing Environment</MotifLabel>}
                  <MotifInput
                    value={form.testingEnvironment || ''}
                    maxLength={MAX_INPUT_LENGTH}
                    onChange={(e: any) => updateForm({ testingEnvironment: e.target.value })}
                  />
                </MotifFormField>

                
              </div>
            </div>

            {/* Dynamic Sections */}
            {activeConfig?.listSections.map(section => {
              const arr = (form[section.key] as KeyedText[]) || [];
              return (
                <div key={section.key} style={{ marginTop: 16 }}>
                  <div className="addApplicationProfile__sectionTitle">{section.title}</div>
                  {submitted && listValidation.sectionMessages?.[section.key] && (
                    <div className="addApplicationProfile__error" role="alert" style={{ marginTop: 6 }}>
                      {listValidation.sectionMessages[section.key]}
                    </div>
                  )}
                  {arr.map((item, index) => (
                    <div className="addApplicationProfile__listRow" key={`${section.key}-${index}`}>
                      <div>
                        <span>{section.valueLabel + (section.isRequired ? ' (required)' : '')}</span>
                        <MotifFormField>
                          {!item.value && <MotifLabel>{section.valueLabel}</MotifLabel>}
                          <MotifInput
                            value={item.value}
                            maxLength={MAX_INPUT_LENGTH}
                            onChange={(e: any) => updateArrayItem(section.key, index, { value: e.target.value })}
                          />
                        </MotifFormField>
                      </div>
                      {section.key === 'endpoints' && (
                        <div>
                          <span>HTTP Method</span>
                          <MotifFormField>
                            {!item.httpMethod && <MotifLabel>Select HTTP Method</MotifLabel>}
                            <MotifSelect
                              value={item.httpMethod}
                              onChange={(v: any) => updateArrayItem(section.key, index, { httpMethod: v })}
                              visibleOptions={4}
                            >
                              <MotifOption value={'GET'}>GET</MotifOption>
                              <MotifOption value={'POST'}>POST</MotifOption>
                              <MotifOption value={'PUT'}>PUT</MotifOption>
                              <MotifOption value={'DELETE'}>DELETE</MotifOption>
                            </MotifSelect>
                          </MotifFormField>
                        </div>
                      )}
                      <div>
                        <span>{section.descLabel}</span>
                        <MotifFormField>
                          {!item.description && <MotifLabel>{section.descLabel}</MotifLabel>}
                          <MotifInput
                            value={item.description || ''}
                            maxLength={MAX_INPUT_LENGTH}
                            onChange={(e: any) => updateArrayItem(section.key, index, { description: e.target.value })}
                            // placeholder={'Please provide Description'}
                          />
                        </MotifFormField>
                      </div>
                      <MotifButton className="addApplicationProfile__deleteBtn" variant="tertiary" size="small" onClick={() => removeArrayItem(section.key, index)} aria-label="Delete row">
                        <MotifIcon src={actionIcDelete24px} /> Delete
                      </MotifButton>
                      {submitted && (listValidation.errors[section.key]?.[index] || '') && (
                        <div className="addApplicationProfile__error addApplicationProfile__rowError" role="alert">
                          {listValidation.errors[section.key]?.[index]}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="addApplicationProfile__addBtnWrap">
                    <MotifButton variant="secondary" size="small" onClick={() => addArrayItem(section.key)}>+ {section.addLabel}</MotifButton>
                  </div>
                  <div className="addApplicationProfile__divider" />
                </div>
              );
            })}

            {/* Additional Text sections */}
            {activeConfig?.showApplicationVersion && (
              <div className="addApplicationProfile__grid">
                <div>
                  <span>Application Version</span>
                  <MotifFormField>
                    {!form.ApplicationVersion && <MotifLabel>Type Application Version</MotifLabel>}
                    <MotifInput value={form.ApplicationVersion || ''} maxLength={MAX_INPUT_LENGTH} onChange={(e: any) => updateForm({ ApplicationVersion: e.target.value })} onBlur={() => markTouched('ApplicationVersion')} />
                  </MotifFormField>
                  {((touched.ApplicationVersion || submitted) && activeConfig?.showApplicationVersion && !!form.ApplicationVersion && !isAppVersion(form.ApplicationVersion || '')) && (
                    <MotifErrorMessage role="alert">Enter a valid version in format X.Y (e.g., 1.0).</MotifErrorMessage>
                  )}
                </div>
              </div>
            )}
            {activeConfig?.showUserRoles && (
              <div className="addApplicationProfile__grid">
                <div>
                  <span>User Roles (required)</span>
                  <MotifFormField>
                    {!form.userRoles && <MotifLabel>Type User Roles</MotifLabel>}
                    <MotifInput value={form.userRoles || ''} maxLength={MAX_INPUT_LENGTH} onChange={(e: any) => updateForm({ userRoles: e.target.value })} onBlur={() => markTouched('userRoles')} />
                  </MotifFormField>
                  {((touched.userRoles || submitted) && !form.userRoles) && (
                    <MotifErrorMessage role="alert">This field is required.</MotifErrorMessage>
                  )}
                </div>
                {/* {activeConfig?.showTestData && (
                  <div>
                    <span>User Test Data Provided</span>
                    <MotifFormField>
                      {!form.testData && <MotifLabel>User Test Data Provided</MotifLabel>}
                      <MotifInput value={form.testData || ''} onChange={(e: any) => setForm(prev => ({ ...prev, testData: e.target.value }))} placeholder={'Enter Test Data'} />
                    </MotifFormField>
                  </div>
                )} */}
              </div>
            )}

            {activeConfig?.showDescription && (
              <div style={{ marginTop: 8 }}>
                <span>Description</span>
                <TextArea label={form.description ? '' : 'Please provide description'} value={form.description || ''} onChange={(e: any) => updateForm({ description: e.target.value })} maxLength={500} />
              </div>
            )}
          </MotifAccordionContent>
            {(() => {
              const topLevelInvalid = !form.testType || !form.applicationName || !form.businessUnit || !form.userRoles || (isAiaTenant && !String(form.workOrder ?? '').trim()) || (activeConfig?.showApplicationVersion && form.ApplicationVersion && !isAppVersion(form.ApplicationVersion || ''));
              const isSaveDisabled = topLevelInvalid || (submitted && !listValidation.isValid);
              return (
                <div className="addApplicationProfile__actionsBar">
                  <MotifButton variant="secondary" size="medium" type="button" onClick={onCancel}>Cancel</MotifButton>
                  <MotifButton
                    variant="primary"
                    size="medium"
                    type="button"
                    disabled={isSaveDisabled}
                    onClick={handleSubmit}
                  >
                    {mode === 'edit' ? 'Save' : 'Save'}
                  </MotifButton>
                </div>
              );
            })()}
        </MotifAccordion>
      </div>
    </div>
  );
};

export default AddApplicationProfile;
